<script setup>
import {
  computed,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CampaignCenterAPI from '../api';
import {
  audienceSummary,
  formatDateTime,
  formatNumber,
  isRunning,
} from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import StatusBadge from '../components/StatusBadge.vue';
import ProgressCard from '../components/ProgressCard.vue';
import MetricsGrid from '../components/MetricsGrid.vue';
import RecipientsTable from '../components/RecipientsTable.vue';
import ActivityList from '../components/ActivityList.vue';
import SendSettingsForm from '../components/SendSettingsForm.vue';
import MessagePreview from '../components/MessagePreview.vue';
import TestSendDialog from '../components/TestSendDialog.vue';

const props = defineProps({
  channel: { type: String, required: true },
  campaignId: { type: [String, Number], required: true },
});
const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const TABS = ['overview', 'recipients', 'report', 'settings', 'activity'];
const campaign = ref(null);
const events = ref([]);
const isLoading = ref(true);
const tab = ref(TABS.includes(route.query.tab) ? route.query.tab : 'overview');
const refreshKey = ref(0);
const confirm = ref({ action: null, title: '', description: '', label: '' });
const confirmDialogRef = ref(null);
const testDialogRef = ref(null);
const settingsForm = ref({
  rate_per_minute: 20,
  window_enabled: false,
  window_start: 8,
  window_end: 18,
  conversation_status: 'pending',
});
const settingsFormRef = ref(null);
const isSavingSettings = ref(false);
const accountSettings = ref({
  limits: { max_rate_per_minute: 200 },
  timezone: 'UTC',
});
let pollTimer = null;
const POLL_INTERVAL = 5000;

const syncSettingsForm = data => {
  const s = data.settings || {};
  settingsForm.value = {
    rate_per_minute: s.rate_per_minute,
    window_enabled: s.window_start !== null && s.window_start !== undefined,
    window_start: s.window_start ?? 8,
    window_end: s.window_end ?? 18,
    conversation_status: s.conversation_status || 'pending',
  };
};

const load = async ({ silent = false } = {}) => {
  if (!silent) isLoading.value = true;
  try {
    const { data } = await CampaignCenterAPI.get(props.campaignId);
    campaign.value = data;
    if (!silent) syncSettingsForm(data);
    refreshKey.value += 1;
  } finally {
    isLoading.value = false;
  }
};
const loadEvents = async () => {
  const { data } = await CampaignCenterAPI.events(props.campaignId);
  events.value = data.payload;
};
// The page lives inside the campaigns keep-alive router view: (re)load per route and poll only while active.
let loadedPath = null;
const startPolling = () => {
  clearInterval(pollTimer);
  pollTimer = setInterval(() => {
    if (campaign.value && isRunning(campaign.value.status)) {
      load({ silent: true });
      if (tab.value === 'activity') loadEvents();
    }
  }, POLL_INTERVAL);
};
const init = async () => {
  loadedPath = route.fullPath;
  campaign.value = null;
  events.value = [];
  tab.value = TABS.includes(route.query.tab) ? route.query.tab : 'overview';
  try {
    const { data } = await CampaignCenterAPI.settings();
    accountSettings.value = data;
  } catch {
    // defaults
  }
  await load();
  loadEvents();
};
onActivated(() => {
  if (!loadedPath || loadedPath.split('?')[0] !== route.fullPath.split('?')[0])
    init();
  else load({ silent: true });
  startPolling();
});
onDeactivated(() => clearInterval(pollTimer));
onBeforeUnmount(() => clearInterval(pollTimer));
watch(tab, value => {
  router.replace({ query: value === 'overview' ? {} : { tab: value } });
  if (value === 'activity') loadEvents();
});

const c = computed(() => campaign.value || {});

// WhatsApp delivery analytics are Chatwoot's own page (v4.17.0); it reads the same
// `campaign_recipients` rows the Wasapi engine writes.
const openOfficialAnalytics = () =>
  router.push({
    name: 'campaigns_whatsapp_analytics',
    params: {
      accountId: route.params.accountId,
      campaignId: c.value.display_id,
    },
  });
const metrics = computed(() => c.value.metrics || {});
const actions = computed(() => c.value.actions || []);
const finished = computed(() =>
  ['completed', 'cancelled', 'failed'].includes(c.value.status)
);
const settingsEditable = computed(() => !finished.value);
const template = computed(() => {
  if (
    props.channel !== 'whatsapp' ||
    !c.value.inbox ||
    !c.value.template_params?.name
  )
    return null;
  const list = getFilteredWhatsAppTemplates.value(c.value.inbox.id) || [];
  return (
    list.find(
      tpl =>
        tpl.name === c.value.template_params.name &&
        (!c.value.template_params.language ||
          tpl.language === c.value.template_params.language)
    ) || null
  );
});
const num = v => formatNumber(v, locale.value);
const fmt = v => formatDateTime(v, locale.value);
const errorMessage = error =>
  error?.response?.data?.error || t('WASAPI_CAMPAIGNS.TOAST.ERROR');
const goBack = () =>
  router.push({
    name:
      props.channel === 'sms'
        ? 'campaigns_sms_index'
        : 'campaigns_whatsapp_index',
  });
const edit = () =>
  router.push({
    name: 'wasapi_campaign_edit',
    params: { channel: props.channel, campaignId: props.campaignId },
  });

const run = async (fn, toastKey) => {
  try {
    const { data } = await fn();
    campaign.value = data;
    syncSettingsForm(data);
    refreshKey.value += 1;
    useAlert(t(`WASAPI_CAMPAIGNS.TOAST.${toastKey}`));
    loadEvents();
    return data;
  } catch (error) {
    useAlert(errorMessage(error));
    return null;
  }
};
const ask = (action, key, label) => {
  confirm.value = {
    action,
    title: t(`WASAPI_CAMPAIGNS.CONFIRM.${key}_TITLE`),
    description: t(`WASAPI_CAMPAIGNS.CONFIRM.${key}_DESC`, {
      title: c.value.title,
    }),
    label,
  };
  confirmDialogRef.value.open();
};
const runConfirmed = async () => {
  const action = confirm.value.action;
  confirmDialogRef.value.close();
  if (action === 'delete') {
    try {
      await CampaignCenterAPI.destroy(props.campaignId);
      useAlert(t('WASAPI_CAMPAIGNS.TOAST.DELETED'));
      goBack();
    } catch (error) {
      useAlert(errorMessage(error));
    }
    return;
  }
  if (action === 'cancel')
    await run(() => CampaignCenterAPI.cancel(props.campaignId), 'CANCELLED');
  if (action === 'pause')
    await run(() => CampaignCenterAPI.pause(props.campaignId), 'PAUSED');
  if (action === 'unschedule')
    await run(
      () => CampaignCenterAPI.unschedule(props.campaignId),
      'UNSCHEDULED'
    );
};
const resume = () =>
  run(() => CampaignCenterAPI.resume(props.campaignId), 'RESUMED');
const schedule = () =>
  run(
    () => CampaignCenterAPI.schedule(props.campaignId, c.value.scheduled_at),
    'SCHEDULED'
  );
const duplicate = async () => {
  try {
    const { data } = await CampaignCenterAPI.duplicate(props.campaignId);
    useAlert(t('WASAPI_CAMPAIGNS.TOAST.DUPLICATED'));
    router.push({
      name: 'wasapi_campaign_edit',
      params: { channel: props.channel, campaignId: data.id },
    });
  } catch (error) {
    useAlert(errorMessage(error));
  }
};
const sendTest = async (phoneNumber, resolve, reject) => {
  try {
    const { data } = await CampaignCenterAPI.testSend(props.campaignId, {
      phoneNumber,
    });
    useAlert(t('WASAPI_CAMPAIGNS.TOAST.TEST_SENT'), {
      type: 'link',
      to: {
        name: 'inbox_conversation',
        params: { conversation_id: data.conversation_id },
      },
      message: t('WASAPI_CAMPAIGNS.TEST.SENT'),
    });
    loadEvents();
    resolve();
  } catch (error) {
    useAlert(errorMessage(error));
    reject(error);
  }
};
const saveSettings = async () => {
  if (settingsFormRef.value?.isValid === false) return;
  isSavingSettings.value = true;
  try {
    const s = settingsForm.value;
    const { data } = await CampaignCenterAPI.update(props.campaignId, {
      trigger_rules: {
        wasapi: {
          ...(c.value.settings?.segment
            ? { segment: c.value.settings.segment }
            : {}),
          rate_per_minute: s.rate_per_minute,
          window_start: s.window_enabled ? s.window_start : null,
          window_end: s.window_enabled ? s.window_end : null,
          conversation_status: s.conversation_status,
        },
      },
    });
    campaign.value = data;
    useAlert(t('WASAPI_CAMPAIGNS.SETTINGS.SAVED'));
    loadEvents();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSavingSettings.value = false;
  }
};

const overviewRows = computed(() => [
  ['CHANNEL', t(`WASAPI_CAMPAIGNS.CHANNEL.${props.channel}`)],
  ['INBOX', c.value.inbox?.name || '—'],
  [
    'SCHEDULED_AT',
    c.value.status === 'draft' ? '—' : fmt(c.value.scheduled_at),
  ],
  ['CREATED_BY', c.value.sender?.name || '—'],
  ['CREATED_AT', fmt(c.value.created_at)],
  ['AUDIENCE', audienceSummary(c.value.audience, c.value.settings?.segment, t)],
]);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="flex flex-col w-full max-w-6xl gap-5 px-6 py-6 mx-auto">
      <div v-if="isLoading && !campaign" class="flex justify-center py-16">
        <Spinner />
      </div>
      <template v-else-if="campaign">
        <header class="flex flex-wrap items-start justify-between gap-3">
          <div class="flex items-start gap-3 min-w-0">
            <Button
              icon="i-lucide-arrow-left"
              variant="ghost"
              color="slate"
              size="sm"
              @click="goBack"
            />
            <div class="flex flex-col min-w-0 gap-1">
              <h1
                class="flex items-center gap-2 text-xl font-medium text-n-slate-12"
              >
                <span class="truncate">{{ c.title }}</span>
                <StatusBadge :status="c.status" size="lg" />
              </h1>
              <p class="text-sm text-n-slate-11 truncate">
                {{ t(`WASAPI_CAMPAIGNS.CHANNEL.${channel}`) }} ·
                {{ c.inbox?.name }}
                <template v-if="c.template_name">
                  ·
                  {{
                    t('WASAPI_CAMPAIGNS.LIST.TEMPLATE', {
                      name: c.template_name,
                    })
                  }}
                </template>
              </p>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <Button
              v-if="channel === 'whatsapp' && c.display_id"
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.ANALYTICS')"
              icon="i-lucide-chart-no-axes-column"
              variant="faded"
              size="sm"
              @click="openOfficialAnalytics"
            />
            <Button
              v-if="actions.includes('edit')"
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.EDIT')"
              icon="i-lucide-pencil"
              variant="faded"
              color="slate"
              size="sm"
              @click="edit"
            />
            <Button
              v-if="actions.includes('test_send')"
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.TEST_SEND')"
              icon="i-lucide-send"
              variant="faded"
              color="slate"
              size="sm"
              @click="testDialogRef.open()"
            />
            <Button
              v-if="actions.includes('schedule')"
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.SCHEDULE')"
              icon="i-lucide-calendar-check"
              color="blue"
              size="sm"
              :disabled="(c.readiness || []).length > 0"
              @click="schedule"
            />
            <Button
              v-if="actions.includes('unschedule')"
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.UNSCHEDULE')"
              icon="i-lucide-calendar-off"
              variant="faded"
              color="slate"
              size="sm"
              @click="
                ask(
                  'unschedule',
                  'UNSCHEDULE',
                  t('WASAPI_CAMPAIGNS.ACTIONS.UNSCHEDULE')
                )
              "
            />
            <Button
              v-if="actions.includes('pause')"
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.PAUSE')"
              icon="i-lucide-pause"
              variant="faded"
              color="amber"
              size="sm"
              @click="
                ask('pause', 'PAUSE', t('WASAPI_CAMPAIGNS.ACTIONS.PAUSE'))
              "
            />
            <Button
              v-if="actions.includes('resume')"
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.RESUME')"
              icon="i-lucide-play"
              color="teal"
              size="sm"
              @click="resume"
            />
            <Button
              v-if="actions.includes('cancel')"
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.CANCEL')"
              icon="i-lucide-x-circle"
              variant="faded"
              color="ruby"
              size="sm"
              @click="
                ask('cancel', 'CANCEL', t('WASAPI_CAMPAIGNS.ACTIONS.CANCEL'))
              "
            />
            <Button
              v-if="actions.includes('duplicate')"
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.DUPLICATE')"
              icon="i-lucide-copy"
              variant="faded"
              color="slate"
              size="sm"
              @click="duplicate"
            />
            <Button
              v-if="actions.includes('delete')"
              icon="i-lucide-trash"
              variant="faded"
              color="ruby"
              size="sm"
              :title="t('WASAPI_CAMPAIGNS.ACTIONS.DELETE')"
              @click="
                ask('delete', 'DELETE', t('WASAPI_CAMPAIGNS.ACTIONS.DELETE'))
              "
            />
          </div>
        </header>

        <nav
          class="flex items-center gap-1 overflow-x-auto border-b border-n-weak whitespace-nowrap"
        >
          <button
            v-for="item in TABS"
            :key="item"
            type="button"
            class="px-3 py-2 -mb-px text-sm border-b-2 transition-colors"
            :class="
              tab === item
                ? 'border-n-brand text-n-slate-12 font-medium'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="tab = item"
          >
            {{ t(`WASAPI_CAMPAIGNS.DETAIL.TABS.${item}`) }}
          </button>
        </nav>

        <!-- Overview -->
        <div v-if="tab === 'overview'" class="flex flex-col gap-5">
          <ProgressCard v-if="isRunning(c.status)" :campaign="c" />
          <p
            v-if="c.status === 'cancelled'"
            class="p-3 text-sm rounded-lg bg-n-alpha-2 text-n-slate-11"
          >
            {{ t('WASAPI_CAMPAIGNS.DETAIL.OVERVIEW.CANCELLED_NOTE') }}
          </p>
          <p
            v-if="c.status === 'paused'"
            class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
          >
            {{ t('WASAPI_CAMPAIGNS.DETAIL.OVERVIEW.PAUSED_NOTE') }}
          </p>
          <div
            v-if="c.status === 'failed'"
            class="p-3 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
          >
            <strong>{{
              t('WASAPI_CAMPAIGNS.DETAIL.OVERVIEW.FAILED_TITLE')
            }}</strong>
            <span v-if="events.find(e => e.action === 'failed')">
              —
              {{
                events.find(e => e.action === 'failed').metadata?.error
              }}</span
            >
          </div>
          <div
            v-if="(c.readiness || []).length"
            class="p-4 rounded-xl bg-n-amber-3 text-n-amber-11"
          >
            <strong class="text-sm">{{
              t('WASAPI_CAMPAIGNS.DETAIL.OVERVIEW.READINESS_TITLE')
            }}</strong>
            <ul class="mt-1 text-sm list-disc list-inside">
              <li
                v-for="issue in c.readiness"
                :key="issue.code + issue.message"
              >
                {{ issue.message }}
              </li>
            </ul>
          </div>
          <div
            class="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]"
          >
            <div class="flex flex-col gap-5">
              <dl
                class="grid grid-cols-2 gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1 md:grid-cols-3"
              >
                <div
                  v-for="[key, value] in overviewRows"
                  :key="key"
                  class="flex flex-col min-w-0"
                >
                  <dt class="text-xs text-n-slate-11">
                    {{ t(`WASAPI_CAMPAIGNS.DETAIL.OVERVIEW.${key}`) }}
                  </dt>
                  <dd class="text-sm text-n-slate-12 truncate">{{ value }}</dd>
                </div>
              </dl>
              <MetricsGrid
                v-if="finished || isRunning(c.status)"
                :metrics="metrics"
              />
            </div>
            <div
              class="p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
            >
              <MessagePreview
                :channel="channel"
                :template="template"
                :template-params="c.template_params"
                :text="c.message"
              />
            </div>
          </div>
        </div>

        <!-- Recipients -->
        <RecipientsTable
          v-else-if="tab === 'recipients'"
          :campaign-id="campaignId"
          :refresh-key="refreshKey"
        />

        <!-- Report -->
        <div v-else-if="tab === 'report'" class="flex flex-col gap-5">
          <ProgressCard v-if="isRunning(c.status)" :campaign="c" />
          <MetricsGrid :metrics="metrics" />
          <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div
              v-for="key in [
                'DELIVERY_RATE',
                'READ_RATE',
                'FAILURE_RATE',
                'OPT_OUT_RATE',
              ]"
              :key="key"
              class="flex flex-col gap-1 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
            >
              <span class="text-xs uppercase tracking-wide text-n-slate-11">{{
                t(`WASAPI_CAMPAIGNS.DETAIL.METRICS.${key}`)
              }}</span>
              <span class="text-2xl font-semibold text-n-slate-12">
                <template
                  v-if="key === 'READ_RATE' && metrics.read_available === false"
                  >{{
                    t('WASAPI_CAMPAIGNS.DETAIL.METRICS.NOT_AVAILABLE')
                  }}</template
                >
                <template v-else>
                  {{
                    metrics.rates?.[key.replace('_RATE', '').toLowerCase()] ===
                      null ||
                    metrics.rates?.[key.replace('_RATE', '').toLowerCase()] ===
                      undefined
                      ? '—'
                      : `${metrics.rates[key.replace('_RATE', '').toLowerCase()]}%`
                  }}
                </template>
              </span>
            </div>
          </div>
          <div class="flex flex-wrap gap-x-6 gap-y-1 text-xs text-n-slate-11">
            <span
              >{{ t('WASAPI_CAMPAIGNS.DETAIL.METRICS.ELIGIBLE') }}:
              <strong class="text-n-slate-12">{{
                num(metrics.eligible)
              }}</strong></span
            >
            <span
              >{{ t('WASAPI_CAMPAIGNS.DETAIL.METRICS.CANCELLED') }}:
              <strong class="text-n-slate-12">{{
                num(metrics.cancelled)
              }}</strong></span
            >
            <span
              >{{ t('WASAPI_CAMPAIGNS.DETAIL.METRICS.SKIPPED') }}:
              <strong class="text-n-slate-12">{{
                num(metrics.skipped)
              }}</strong></span
            >
          </div>
        </div>

        <!-- Settings -->
        <div
          v-else-if="tab === 'settings'"
          class="flex flex-col max-w-xl gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
        >
          <p class="text-sm text-n-slate-11">
            {{
              finished
                ? t('WASAPI_CAMPAIGNS.DETAIL.SETTINGS_TAB.FINISHED_NOTE')
                : c.started
                  ? t('WASAPI_CAMPAIGNS.DETAIL.SETTINGS_TAB.EDITABLE_NOTE')
                  : t('WASAPI_CAMPAIGNS.SETTINGS.WINDOW_HELP')
            }}
          </p>
          <p v-if="c.started && !finished" class="text-xs text-n-slate-10">
            {{ t('WASAPI_CAMPAIGNS.DETAIL.SETTINGS_TAB.LOCKED_NOTE') }}
          </p>
          <SendSettingsForm
            ref="settingsFormRef"
            v-model="settingsForm"
            :max-rate="accountSettings.limits?.max_rate_per_minute || 200"
            :timezone="accountSettings.timezone || c.settings?.timezone"
            :disabled="!settingsEditable"
          />
          <div v-if="settingsEditable">
            <Button
              :label="t('WASAPI_CAMPAIGNS.ACTIONS.SAVE')"
              size="sm"
              color="blue"
              :is-loading="isSavingSettings"
              @click="saveSettings"
            />
          </div>
        </div>

        <!-- Activity -->
        <ActivityList v-else-if="tab === 'activity'" :events="events" />
      </template>
    </div>

    <Dialog
      ref="confirmDialogRef"
      type="alert"
      :title="confirm.title"
      :description="confirm.description"
      :confirm-button-label="confirm.label"
      :cancel-button-label="t('WASAPI_CAMPAIGNS.CONFIRM.CANCEL')"
      @confirm="runConfirmed"
    />
    <TestSendDialog ref="testDialogRef" @send="sendTest" />
  </section>
</template>
